import { useState } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {ArrowLeft, ArrowRight, Plus, Trash2, Copy, Calendar, CheckCircle2} from 'lucide-react'
import { copiarTexto } from '@/utils/clipboard'
import {
  MAX_CONTATOS_COMPARTILHE,
  ORIENTACAO_CONTATOS,
  MENSAGENS_BEMESTAR,
  MENSAGENS_NEGOCIO,
  type ContatoCompartilhe,
  type CronogramaCompartilhe,
  type FormatoJornada,
  type ItemChecklist,
  contatoVazio,
  montarChecklist14Dias,
  montarMensagemPronta,
  progressoChecklist,
  parseJsonSafe,
  linkDiagnosticoConsultora,
  linkCursoUmaGota,
} from '@/utils/jornadaCompartilhe'

export type EtapaCompartilhe = 'contatos' | 'formato' | 'mensagens' | 'cronograma' | 'concluido' | 'historia'

export interface AcessoCompartilhe {
  _id: string
  token: string
  nomeDestinatario: string
  emailDestinatario: string
  consultora: string
  origem?: string
  status: 'ativo' | 'concluido' | 'inativo'
  etapaAtual: EtapaCompartilhe
  contatosJson?: string
  formatoEscolhido?: FormatoJornada | ''
  cronogramaJson?: string
  linkDiagnosticoGratuito?: string
  createdAt?: string
  updatedAt?: string
}

interface Props {
  acesso: AcessoCompartilhe
  onVoltar: () => void
  onAtualizado: (novo: AcessoCompartilhe) => void
  modoPublico?: boolean
}

function etapaEfetiva(etapa: EtapaCompartilhe): 'contatos' | 'formato' | 'cronograma' {
  if (etapa === 'cronograma' || etapa === 'concluido' || etapa === 'mensagens') return 'cronograma'
  if (etapa === 'formato' || etapa === 'historia') return 'formato'
  return 'contatos'
}

export default function WizardJornadaCompartilhe({ acesso, onVoltar, onAtualizado, modoPublico = false }: Props) {
  const slug = String(acesso.consultora || 'monica').trim() || 'monica'
  const linkDiagnostico = String(acesso.linkDiagnosticoGratuito || '').trim() || linkDiagnosticoConsultora(slug)
  const linkCurso = linkCursoUmaGota(slug)

  const [contatos, setContatos] = useState<ContatoCompartilhe[]>(() => {
    const parsed = parseJsonSafe<ContatoCompartilhe[]>(acesso.contatosJson, [])
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [contatoVazio()]
  })
  const [formatoEscolhido, setFormatoEscolhido] = useState<FormatoJornada | ''>(() => {
    if (acesso.formatoEscolhido === 'individual' || acesso.formatoEscolhido === 'aula') return acesso.formatoEscolhido
    return ''
  })
  const [cronograma, setCronograma] = useState<CronogramaCompartilhe | null>(() =>
    parseJsonSafe<CronogramaCompartilhe | null>(acesso.cronogramaJson, null),
  )
  const [salvando, setSalvando] = useState(false)
  const etapa = etapaEfetiva(acesso.etapaAtual)

  async function persistir(patch: Partial<AcessoCompartilhe>) {
    const agora = new Date().toISOString()
    const dados = { ...patch, updatedAt: agora }
    await lumi.entities.jornada_compartilhe_acessos.update(acesso._id, dados)
    onAtualizado({ ...acesso, ...dados })
  }

  function atualizarContato(id: string, patch: Partial<ContatoCompartilhe>) {
    setContatos((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))
  }

  function adicionarContato() {
    if (contatos.length >= MAX_CONTATOS_COMPARTILHE) {
      toast.error(`Limite de ${MAX_CONTATOS_COMPARTILHE} pessoas nesta jornada.`)
      return
    }
    setContatos((prev) => [...prev, contatoVazio()])
  }

  async function copiar(texto: string) {
    try {
      await copiarTexto(texto)
      toast.success('Mensagem copiada!')
    } catch {
      toast.error('Não foi possível copiar.')
    }
  }

  async function salvarContatosEAvancar() {
    const validos = contatos.filter((c) => String(c.nome || '').trim())
    if (validos.length === 0) {
      toast.error('Adicione ao menos 1 pessoa.')
      return
    }
    setSalvando(true)
    try {
      await persistir({
        contatosJson: JSON.stringify(validos),
        etapaAtual: 'formato',
      })
      setContatos(validos)
      toast.success('Lista salva.')
    } catch (e) {
      console.error('[compartilhe] erro ao salvar contatos', e)
      toast.error('Erro ao salvar contatos')
    } finally {
      setSalvando(false)
    }
  }

  async function gerarChecklistEAvancar() {
    const validos = contatos.filter((c) => String(c.nome || '').trim())
    if (validos.length === 0) {
      toast.error('Adicione ao menos 1 pessoa.')
      return
    }
    const aulaCount = validos.filter((c) => c.abordagem === 'aula').length
    const formato: FormatoJornada =
      formatoEscolhido === 'individual' || formatoEscolhido === 'aula'
        ? formatoEscolhido
        : aulaCount > validos.length / 2
          ? 'aula'
          : 'individual'
    const gerado = montarChecklist14Dias({
      formato,
      contatos: validos,
      linkCursoUmaGota: linkCurso,
    })
    setSalvando(true)
    try {
      await persistir({
        contatosJson: JSON.stringify(validos),
        formatoEscolhido: formato,
        cronogramaJson: JSON.stringify(gerado),
        etapaAtual: 'cronograma',
        status: 'ativo',
      })
      setFormatoEscolhido(formato)
      setCronograma(gerado)
      toast.success('Checklist de 14 dias pronto.')
    } catch (e) {
      console.error('[compartilhe] erro ao gerar checklist', e)
      toast.error('Erro ao gerar o cronograma')
    } finally {
      setSalvando(false)
    }
  }

  async function alternarItem(itemId: string) {
    if (!cronograma) return
    const itens = cronograma.itens.map((item) =>
      item.id === itemId ? { ...item, concluido: !item.concluido } : item,
    )
    const atualizado = { ...cronograma, itens }
    setCronograma(atualizado)
    const { feitos, total } = progressoChecklist(atualizado)
    const concluida = total > 0 && feitos === total
    try {
      await persistir({
        cronogramaJson: JSON.stringify(atualizado),
        status: concluida ? 'concluido' : 'ativo',
        etapaAtual: concluida ? 'concluido' : 'cronograma',
      })
    } catch (e) {
      console.error('[compartilhe] erro ao marcar item', e)
      toast.error('Não foi possível salvar o checklist')
    }
  }

  const Header = (
    <button
      type="button"
      onClick={onVoltar}
      className="inline-flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline mb-6 font-medium"
    >
      <ArrowLeft className="w-4 h-4" />
      {modoPublico ? 'Sair desta jornada' : 'Voltar para minhas jornadas'}
    </button>
  )

  const passos: Array<{ id: 'contatos' | 'formato' | 'cronograma'; label: string }> = [
    { id: 'contatos', label: 'Lista de contatos' },
    { id: 'formato', label: 'Formato e mensagens' },
    { id: 'cronograma', label: 'Checklist 14 dias' },
  ]

  const Progresso = (
    <div className="flex items-center gap-2 mb-6 flex-wrap">
      {passos.map((passo, idx) => {
        const ordem = ['contatos', 'formato', 'cronograma']
        const atualIdx = ordem.indexOf(etapa)
        const passoIdx = ordem.indexOf(passo.id)
        const feito = atualIdx > passoIdx
        const atual = atualIdx === passoIdx
        return (
          <div key={passo.id} className="flex items-center gap-2">
            <span
              className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                feito
                  ? 'bg-[#2D6A4F] text-white'
                  : atual
                    ? 'bg-[#D8F3DC] text-[#2D6A4F] border-2 border-[#2D6A4F]'
                    : 'bg-[#F3F4F6] text-[#9CA3AF]'
              }`}
            >
              {feito ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
            </span>
            <span className={`text-xs font-medium ${atual ? 'text-[#2D6A4F]' : 'text-[#5F5E5A]'}`}>{passo.label}</span>
            {idx < 2 ? <span className="w-4 h-px bg-[#EDE0D4]" /> : null}
          </div>
        )
      })}
    </div>
  )

  if (etapa === 'contatos') {
    return (
      <div className="max-w-3xl">
        {Header}
        {Progresso}
        <div className="bg-[#FEF9E7] border border-[#F5E1A4] rounded-2xl p-4 mb-6 text-sm text-[#7A5C00] leading-relaxed">
          {ORIENTACAO_CONTATOS}
        </div>
        <div className="space-y-4 mb-6">
          {contatos.map((c, idx) => (
            <div key={c.id} className="bg-white border border-[#EDE0D4] rounded-xl p-4 space-y-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#2C2C2A]">Pessoa {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setContatos((prev) => prev.filter((item) => item.id !== c.id))}
                  className="text-red-600 hover:bg-red-50 p-1 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={c.nome}
                onChange={(e) => atualizarContato(c.id, { nome: e.target.value })}
                placeholder="Nome da pessoa"
                className="w-full px-3 py-2 border-2 border-[#EDE0D4] rounded-xl focus:border-[#52B788] focus:outline-none text-sm"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => atualizarContato(c.id, { abordagem: 'individual' })}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border-2 transition-colors ${
                    c.abordagem === 'individual'
                      ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                      : 'border-[#EDE0D4] text-[#5F5E5A]'
                  }`}
                >
                  Individual
                </button>
                <button
                  type="button"
                  onClick={() => atualizarContato(c.id, { abordagem: 'aula' })}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border-2 transition-colors ${
                    c.abordagem === 'aula'
                      ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                      : 'border-[#EDE0D4] text-[#5F5E5A]'
                  }`}
                >
                  Aula
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={adicionarContato}
            disabled={contatos.length >= MAX_CONTATOS_COMPARTILHE}
            className="inline-flex items-center gap-2 border-2 border-[#2D6A4F] text-[#2D6A4F] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#D8F3DC] transition-colors disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Adicionar pessoa ({contatos.length}/{MAX_CONTATOS_COMPARTILHE})
          </button>
          <button
            type="button"
            onClick={() => void salvarContatosEAvancar()}
            disabled={salvando}
            className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors disabled:opacity-60"
          >
            {salvando ? 'Salvando...' : 'Continuar'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    )
  }

  if (etapa === 'formato') {
    return (
      <div className="max-w-3xl">
        {Header}
        {Progresso}
        <div className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-6 mb-6">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F] mb-3">Escolha de formato</h3>
          <p className="text-sm text-[#5F5E5A] mb-4">
            Defina o formato principal desta jornada. Isso organiza o checklist dos 14 dias.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setFormatoEscolhido('individual')}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${
                formatoEscolhido === 'individual'
                  ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                  : 'border-[#EDE0D4] text-[#5F5E5A]'
              }`}
            >
              Conversas individuais
            </button>
            <button
              type="button"
              onClick={() => setFormatoEscolhido('aula')}
              className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${
                formatoEscolhido === 'aula'
                  ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                  : 'border-[#EDE0D4] text-[#5F5E5A]'
              }`}
            >
              Aula ao vivo
            </button>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <h3 className="text-lg font-cormorant font-semibold text-[#2D6A4F]">Primeiros contatos</h3>
          {contatos.filter((c) => c.nome.trim()).map((c) => {
            const modelos = c.foco === 'negocio' ? MENSAGENS_NEGOCIO : c.foco === 'bemestar' ? MENSAGENS_BEMESTAR : []
            return (
              <div key={c.id} className="bg-white rounded-2xl shadow-md border border-[#EDE0D4] p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <p className="font-medium text-[#2C2C2A]">{c.nome}</p>
                    <p className="text-xs text-[#5F5E5A]">{c.abordagem === 'aula' ? 'Aula' : 'Individual'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => atualizarContato(c.id, { foco: 'bemestar' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-colors ${
                        c.foco === 'bemestar'
                          ? 'bg-[#D8F3DC] text-[#2D6A4F] border-[#2D6A4F]'
                          : 'border-[#EDE0D4] text-[#5F5E5A]'
                      }`}
                    >
                      Só bem-estar/diagnóstico
                    </button>
                    <button
                      type="button"
                      onClick={() => atualizarContato(c.id, { foco: 'negocio' })}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-colors ${
                        c.foco === 'negocio'
                          ? 'bg-[#D8F3DC] text-[#2D6A4F] border-[#2D6A4F]'
                          : 'border-[#EDE0D4] text-[#5F5E5A]'
                      }`}
                    >
                      Também negócio
                    </button>
                  </div>
                </div>
                {modelos.length > 0 ? (
                  <div className="space-y-3">
                    {modelos.map((modelo, idx) => {
                      const texto = montarMensagemPronta(modelo, c.nome, linkDiagnostico)
                      return (
                        <div key={`${c.id}-${idx}`} className="bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3">
                          <p className="text-xs text-[#B08968] font-medium mb-1">Mensagem {idx + 1}</p>
                          <p className="text-sm text-[#2C2C2A] whitespace-pre-wrap mb-3">{texto}</p>
                          <button
                            type="button"
                            onClick={() => void copiar(texto)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2D6A4F] hover:underline"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Copiar mensagem
                          </button>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-[#5F5E5A]">Escolha o foco para ver as mensagens prontas.</p>
                )}
              </div>
            )
          })}
        </div>

        <button
          type="button"
          onClick={() => void gerarChecklistEAvancar()}
          disabled={salvando}
          className="inline-flex items-center gap-2 bg-[#2D6A4F] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-opacity-90 transition-colors disabled:opacity-60"
        >
          {salvando ? 'Gerando checklist...' : 'Gerar checklist de 14 dias'}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    )
  }

  const { feitos, total } = progressoChecklist(cronograma)
  const itensPorDia = new Map<number, ItemChecklist[]>()
  ;(cronograma?.itens || []).forEach((item) => {
    const lista = itensPorDia.get(item.dia) || []
    lista.push(item)
    itensPorDia.set(item.dia, lista)
  })
  const dias = Array.from(itensPorDia.keys()).sort((a, b) => a - b)

  return (
    <div className="max-w-3xl">
      {Header}
      {Progresso}
      <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-4 mb-6">
        <p className="text-sm text-[#2D6A4F] font-medium">
          {feitos} de {total} itens concluídos
          {acesso.status === 'concluido' ? ' · Jornada concluída' : ''}
        </p>
      </div>
      <h3 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5" />
        Cronograma e checklist de 14 dias
      </h3>
      <div className="space-y-3 mb-8">
        {dias.map((dia) => (
          <div key={dia} className="bg-white rounded-xl border border-[#EDE0D4] p-4">
            <p className="text-xs uppercase tracking-wide text-[#B08968] font-semibold mb-3">Dia {dia}</p>
            <div className="space-y-2">
              {(itensPorDia.get(dia) || []).map((item) => (
                <label key={item.id} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.concluido}
                    onChange={() => void alternarItem(item.id)}
                    className="mt-1 w-4 h-4 accent-[#2D6A4F]"
                  />
                  <span className={`text-sm leading-relaxed ${item.concluido ? 'text-[#5F5E5A] line-through' : 'text-[#2C2C2A]'}`}>
                    {item.texto}
                  </span>
                </label>
              ))}
            </div>
            {(itensPorDia.get(dia) || []).some((item) => item.tipo === 'curso' && item.linkCurso) ? (
              <div className="mt-3 ml-7 bg-[#F8F4EF] border border-[#EDE0D4] rounded-xl px-4 py-3">
                <p className="text-xs text-[#B08968] font-medium mb-1">Link rastreável — Uma Gota de Equilíbrio</p>
                <p className="text-sm text-[#2C2C2A] break-all mb-2">{linkCurso}</p>
                <button
                  type="button"
                  onClick={() => void copiar(linkCurso)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2D6A4F] hover:underline"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copiar link do curso
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  )
}
